// =====================================================
// REPORTE "PLAN PRODUCTIVO" (BDSJEA-466): impresión / PDF y descarga a Excel
// src/pages/Agro/Productor/reportePlanProductivoExport.js
// - tituloPlanProductivo / imprimirReporte: «Imprimir / PDF» por el navegador (window.print()), con el título del documento
//   temporal «Plan Productivo - <razón social> - <campaña>» (es el nombre que sugiere «Guardar como PDF»), la clase
//   body.rpp-imprimiendo (que activa los estilos de impresión del reporte) y el @page A4 apaisado, todo solo mientras se imprime.
// - armarLibroPlanProductivo / descargarExcelPlanProductivo: .xlsx (SheetJS, ya usado en el front) con las mismas secciones
//   y números del reporte; los números van como número (tipo n) con formato de miles/decimales de Excel.
// =====================================================

import * as XLSX from "xlsx"

import { formatearCuit, formatearFecha } from "helpers/formatters"
import {
  ND,
  NA,
  MAQUINARIA,
  destinoDe,
  decimalesHa,
  ha,
  idOrigenDe,
  nivelRendimientoDe,
  nivelesRendimientoDe,
  comercializadasTexto,
} from "./reportePlanProductivoComun"

// Caracteres inválidos en nombres de archivo de Windows/Linux/macOS y controles
const INVALIDOS = /[\\/:*?"<>|\u0000-\u001f]/g
const MAX_NOMBRE = 120

export const sanitizarNombreArchivo = texto =>
  String(texto)
    .replace(INVALIDOS, "-")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[. ]+$/, "")

// Corta en el último espacio antes del tope (sin partir palabras) salvo que no haya espacio razonable
const acortar = (texto, max) => {
  if (texto.length <= max) return texto
  const corte = texto.slice(0, max)
  const i = corte.lastIndexOf(" ")
  return (i > max / 2 ? corte.slice(0, i) : corte).trim()
}

// «Plan Productivo - <razón social> - <campaña>» (tope ~120 caracteres, recortando la razón social); si falta alguno
// se omite (nunca «undefined»)
export const tituloPlanProductivo = r => {
  const campana = r?.campanas?.length
    ? sanitizarNombreArchivo(r.campanas.join(", "))
    : ""
  let razon = r?.razonSocial ? sanitizarNombreArchivo(r.razonSocial) : ""
  const fijo = ["Plan Productivo", campana].filter(Boolean).join(" - ").length
  razon = acortar(razon, Math.max(20, MAX_NOMBRE - fijo - 6))
  return (
    ["Plan Productivo", razon, campana].filter(Boolean).join(" - ") ||
    "Plan Productivo"
  )
}

// ---- Impresión ----
const CLASE_IMPRIMIENDO = "rpp-imprimiendo"
const ID_ESTILO_PAGINA = "rpp-estilo-pagina"

// Estado de la impresión en curso (módulo): el título original se guarda una sola vez, así un segundo clic mientras
// se imprime no captura el título temporal como «previo».
let impresion = null

const terminarImpresion = () => {
  if (!impresion) return
  document.title = impresion.tituloPrevio
  document.body.classList.remove(CLASE_IMPRIMIENDO)
  impresion.estilo.remove()
  window.removeEventListener("afterprint", terminarImpresion)
  impresion = null
}

// Abre el diálogo de impresión del navegador (imprimir o «Guardar como PDF»). Título, clase del body y @page A4 apaisado
// (global solo durante la impresión) se restauran al volver de print() (aunque lance) y también en «afterprint».
export const imprimirReporte = r => {
  if (!impresion) {
    const estilo = document.createElement("style")
    estilo.id = ID_ESTILO_PAGINA
    estilo.textContent = "@page { size: A4 landscape; margin: 10mm; }"
    document.head.appendChild(estilo)
    impresion = { tituloPrevio: document.title, estilo }
  }
  document.title = tituloPlanProductivo(r)
  document.body.classList.add(CLASE_IMPRIMIENDO)
  window.addEventListener("afterprint", terminarImpresion)
  try {
    window.print()
  } finally {
    terminarImpresion()
  }
}

// ---- Excel ----

// Número como número de Excel con la MISMA precisión que muestra la pantalla (el valor se redondea a lo visible para que
// las sumas en Excel coincidan). Sin dato (null/undefined) = `vacio` («—» en las columnas que la pantalla muestra así, «-» en
// el resto); texto no numérico o NaN = «-». Un 0 sigue siendo 0.
const aNumero = v => {
  if (typeof v === "number") return v
  if (typeof v === "string" && v.trim() !== "") return Number(v)
  return NaN
}

const num = (v, { vacio = "-", entero = false } = {}) => {
  if (v == null) return vacio
  const n = aNumero(v)
  if (!Number.isFinite(n)) return "-"
  const dec = entero ? 0 : decimalesHa(n)
  const k = 10 ** dec
  return {
    t: "n",
    v: Math.round(n * k) / k,
    z: dec === 0 ? "#,##0" : "#,##0.00",
  }
}

// Hectáreas que la pantalla muestra «—» si no hay dato (haONa)
const haNa = v => num(v, { vacio: NA })

const porc = v => {
  if (v == null) return "-"
  const n = aNumero(v)
  return Number.isFinite(n) ? { t: "n", v: Math.round(n) / 100, z: "0%" } : "-"
}

const filaDestino = f => [
  idOrigenDe(f),
  num(f.haPropias),
  num(f.haArrendadas),
  num(f.haTotales),
  destinoDe(f) || "-",
  f.localidad || "-",
  f.departamento || "-",
  f.provincia || "-",
  nivelRendimientoDe(f),
]

const filasTotales = t => [
  ["Totales", num(t.haPropias), num(t.haArrendadas), num(t.haTotales)],
  [
    "%",
    porc(t.porcentajePropias),
    porc(t.porcentajeArrendadas),
    porc(t.porcentajeTotal),
  ],
]

export const armarLibroPlanProductivo = r => {
  const filas = []
  const t = r.totalesEstablecimientos
  const campos = r.camposImproductivos

  filas.push(["Plan Productivo"])
  filas.push([
    r.razonSocial || NA,
    `CUIT ${r.cuit ? formatearCuit(r.cuit) : NA}`,
  ])
  filas.push([
    "Fecha de declaración",
    r.fechaDeclaracion ? formatearFecha(r.fechaDeclaracion) : ND,
  ])
  if (r.fechaDeclaracion && r.fechaDeclaracionOrigen)
    filas.push([`Fecha de declaración: ${r.fechaDeclaracionOrigen}`])
  filas.push([
    "Campaña",
    r.campanas && r.campanas.length > 0 ? r.campanas.join(", ") : ND,
  ])
  filas.push([])

  filas.push(["Resumen de Establecimientos"])
  const resumen = r.resumenEstablecimientos || []
  if (resumen.length === 0 && !(campos && campos.filas)) {
    filas.push(["sin datos"])
  } else {
    filas.push([
      "Id",
      "Has Propias",
      "Has Arrendadas",
      "Has Totales",
      "Destino",
      "Localidad",
      "Departamento",
      "Provincia",
      "Rendimiento",
    ])
    resumen.forEach(f => filas.push(filaDestino(f)))
    if (t && resumen.length > 0) filas.push(...filasTotales(t))
    if (campos) {
      ;(campos.filas || []).forEach(f => filas.push(filaDestino(f)))
      if (campos.totales) filas.push(...filasTotales(campos.totales))
    }
  }
  if (t && t.haSinTenencia > 0)
    filas.push([
      `${ha(t.haSinTenencia)} ha sin tenencia cargada figuran solo en Has Totales.`,
    ])
  filas.push([])

  filas.push(["Agricultura"])
  const agricultura = r.agricultura || []
  if (agricultura.length === 0) {
    filas.push(["sin datos"])
  } else {
    filas.push([
      "Cultivo",
      "Has Propias",
      "Has Arrendadas",
      "Total Has por Cultivo",
      "Rendimiento",
    ])
    agricultura.forEach(c =>
      filas.push([
        c.cultivo,
        haNa(c.haPropias),
        haNa(c.haArrendadas),
        num(c.haTotales),
        nivelesRendimientoDe(c),
      ]),
    )
    filas.push([
      "Totales",
      haNa(r.agriculturaTotalHaPropias),
      haNa(r.agriculturaTotalHaArrendadas),
      num(r.agriculturaTotalHa),
    ])
    filas.push(["Maquinaria Propia", MAQUINARIA[r.maquinariaPropia] || ND])
  }
  filas.push([])

  filas.push(["Declaración de Stock"])
  const stock = r.stock || []
  if (stock.length === 0) {
    filas.push(["sin datos"])
  } else {
    filas.push(["Cultivo", "(qq)", "Valoración"])
    stock.forEach(s =>
      filas.push([
        s.cultivo,
        num(s.cantidadQq),
        num(s.valuacionUsd, { entero: true }),
      ]),
    )
    filas.push([
      "Totales",
      num(r.stockTotalQq),
      num(r.stockTotalValuacionUsd, { entero: true }),
    ])
  }
  filas.push([])

  filas.push(["Ganadería"])
  const ganaderia = r.ganaderia || []
  if (ganaderia.length === 0 && r.ganaderiaHaTotales == null) {
    filas.push(["sin datos"])
  } else {
    filas.push([
      "Actividad",
      "Has Propias",
      "Has Arrendadas",
      "Total Has",
      "Cabezas madres",
      "Cabezas para la venta",
    ])
    if (ganaderia.length === 0) filas.push(["sin datos", NA, NA, NA, NA, NA])
    ganaderia.forEach(g =>
      filas.push([
        g.actividad,
        haNa(g.haPropias),
        haNa(g.haArrendadas),
        haNa(g.haTotales),
        num(g.cabezasMadres),
        num(g.cabezasVenta),
      ]),
    )
    filas.push([
      "Totales",
      haNa(r.ganaderiaHaPropias),
      haNa(r.ganaderiaHaArrendadas),
      haNa(r.ganaderiaHaTotales),
      num(r.ganaderiaTotalCabezasMadres),
      num(r.ganaderiaTotalCabezasVenta),
    ])
  }
  if (r.ganaderiaHaNota) filas.push([r.ganaderiaHaNota])
  filas.push([
    "Cantidad de cabezas comercializadas por año Ganado Bovino",
    comercializadasTexto(r) != null
      ? num(r.cabezasComercializadasAnio, { entero: true })
      : ND,
    ...(comercializadasTexto(r) != null && r.cabezasComercializadasCampana
      ? [`campaña ${r.cabezasComercializadasCampana}`]
      : []),
  ])
  filas.push(["Tambo", r.tambos > 0 ? `${r.tambos} registro(s)` : "sin datos"])

  if (r.noDisponible && r.noDisponible.length > 0) {
    filas.push([])
    filas.push(["No disponible en el sistema nuevo:"])
    r.noDisponible.forEach(n => filas.push([n]))
  }

  const hoja = XLSX.utils.aoa_to_sheet(filas)
  hoja["!cols"] = [
    { wch: 34 },
    { wch: 16 },
    { wch: 16 },
    { wch: 22 },
    { wch: 38 },
    { wch: 24 },
    { wch: 22 },
    { wch: 18 },
    { wch: 42 },
  ]
  const libro = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(libro, hoja, "Plan Productivo")
  return libro
}

export const descargarExcelPlanProductivo = r => {
  XLSX.writeFile(armarLibroPlanProductivo(r), `${tituloPlanProductivo(r)}.xlsx`)
}
